import type { components } from '@/api/schema';
import { asRecord } from '@/lib/json';

type ToolDto = components['schemas']['ToolDto'];
type SideEffect = components['schemas']['ToolSideEffect'];

function typeOf(s: Record<string, unknown>): string {
  const t = s['type'];
  if (Array.isArray(t)) return t.filter((x) => x !== 'null').join(' | ') + (t.includes('null') ? '?' : '');
  if (typeof t === 'string') return t;
  if (s['enum']) return 'enum';
  if (s['$ref']) return String(s['$ref']).split('/').pop() ?? 'ref';
  if (s['oneOf'] || s['anyOf']) return 'union';
  return 'any';
}

export interface SchemaNodeInfo {
  name: string;
  path: string;
  type: string;
  required: boolean;
  description?: string;
  children: SchemaNodeInfo[];
}

/**
 * Flattens a JSON Schema into a tree with JSON paths (`$.items[*].candidateId`) for "copy path" wiring in the
 * designer (08 §2.3). Depth-limited; unresolved `$ref`s are shown by name.
 */
export function schemaToTree(schema: unknown, path = '$', depth = 0): SchemaNodeInfo[] {
  const s = asRecord(schema);
  if (depth > 6) return [];
  const props = asRecord(s['properties']);
  const required = new Set(Array.isArray(s['required']) ? (s['required'] as string[]) : []);
  const out: SchemaNodeInfo[] = [];
  for (const [name, raw] of Object.entries(props)) {
    const child = asRecord(raw);
    const childPath = `${path}.${name}`;
    const type = typeOf(child);
    let children: SchemaNodeInfo[] = [];
    if (child['properties']) children = schemaToTree(child, childPath, depth + 1);
    else if (child['items']) children = schemaToTree(child['items'], `${childPath}[*]`, depth + 1);
    out.push({
      name,
      path: childPath,
      type: type === 'array' && child['items'] ? `${typeOf(asRecord(child['items']))}[]` : type,
      required: required.has(name),
      description: typeof child['description'] === 'string' ? child['description'] : undefined,
      children,
    });
  }
  return out;
}

export function sideEffectVariant(s: SideEffect): 'default' | 'warning' | 'destructive' {
  return s === 'authoritative' ? 'destructive' : s === 'staged' ? 'warning' : 'default';
}

/** Groups tools by their first tag (resource), the way the designer palette does (08 §2.1). */
export function groupByTag(tools: readonly ToolDto[]): [string, ToolDto[]][] {
  const groups = new Map<string, ToolDto[]>();
  for (const tool of tools) {
    const tag = tool.tags[0] ?? tool.source;
    groups.set(tag, [...(groups.get(tag) ?? []), tool]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}
